import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import PerformanceUserActivityTable from "../../components/shared/Performance/PerformanceUserActivityTable";
import PerformanceUserActivityGraph from "../../components/shared/Performance/PerformanceUserActivityGraph";
import { ActivityMetricKey, DEFAULT_ACTIVITY_METRIC } from "../../components/shared/Performance/activityMetrics";
import usePerformance from "../../data/usePerformance";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import toIsoDate from "../../utils/toIsoDate";

export default function Performance() {
  const { setInfo } = usePageNameContext();

  const now = DateTime.now();
  const [dates, setDates] = useState<DateRangeType>({
    startDate: now.startOf("month").toJSDate(),
    endDate: now.toJSDate(),
  });
  const [selectedMetric, setSelectedMetric] = useState<ActivityMetricKey>(DEFAULT_ACTIVITY_METRIC);

  useEffect(() => {
    setInfo({
      name: `Performance`,
      desc: `Personnel activity and performance metrics`,
    });
  }, [setInfo]);

  const from = useMemo(() => toIsoDate(dates.startDate), [dates.startDate]);
  const to = useMemo(() => toIsoDate(dates.endDate), [dates.endDate]);

  const { data, isLoading } = usePerformance(from, to);

  // Inclusive day count for the selected range, used to work out daily averages.
  const daysInPeriod = useMemo(() => {
    if (!from || !to) return 1;
    const start = DateTime.fromISO(from);
    const end = DateTime.fromISO(to);
    return Math.max(1, Math.floor(end.diff(start, "days").days) + 1);
  }, [from, to]);

  const stats = data?.data;
  // Only show the loading state on first load (matches the Dashboard); focus refetches update in place.
  const isLoadingStats = isLoading || !stats;

  return (
    <div className="mt-4 grid grid-cols-1 gap-4">
      <PerformanceUserActivityTable
        title="Personnel Activity"
        rowLabelColumn="User"
        stats={stats}
        daysInPeriod={daysInPeriod}
        isLoading={isLoadingStats}
        dates={dates}
        onDatesChange={setDates}
        selectedMetric={selectedMetric}
        onMetricSelect={setSelectedMetric}
      />
      <PerformanceUserActivityGraph
        title="Activity by User"
        stats={stats}
        isLoading={isLoadingStats}
        selectedMetric={selectedMetric}
        onMetricChange={setSelectedMetric}
      />
    </div>
  );
}
